"use client";
import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import {
  AlertCircle,
  Camera,
  Eye,
  Loader2,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import { useStableMemoComponent } from "../../../hooks/use-stable-memo-component";
import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { sharedConstructionAspectsProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { OfflineImage } from "../../shared/offline-image";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";
import { LoadingIndicator } from "../../ui/loading-indicator";
import { OptimizedCheckbox, OptimizedTextarea } from "../optimized-form-field";
import { ImageCapturePicker } from "./image-capture-picker";
import { LocalSectionImportControl } from "./local-section-import-control";

interface ConstructionAspectsFormProps extends AuditFormProps {
  siteId: string;
  templateId: string;
  reportId?: string;
  readOnly?: boolean;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  previousCompletedReports?: any[];
}

interface ConstructionImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface ConstructionAspectsData {
  // Text fields
  constructionAccessDescription: string;
  otherRemarks: string;

  // Boolean fields
  accessibleBy38tLorries: boolean;
  groundConditionRisks: boolean;
  trafficImpact: boolean;
  offloadingAreaAvailable: boolean;
  waterPowerAvailability: boolean;

  // Images
  constructionAspectsImages: ConstructionImage[];
}

const initialData: ConstructionAspectsData = {
  constructionAccessDescription: "",
  otherRemarks: "",
  accessibleBy38tLorries: false,
  groundConditionRisks: false,
  trafficImpact: false,
  offloadingAreaAvailable: false,
  waterPowerAvailability: false,
  constructionAspectsImages: [],
};

export const ConstructionAspectsForm = memo(function ConstructionAspectsForm({
  siteId,
  templateId,
  reportId,
  sessionProgress,
  setSessionProgress,
  readOnly = false,
  previousCompletedReports = [],
}: ConstructionAspectsFormProps) {
  const { notify } = useNotification();
  const { loading } = useAppSelector((state) => state?.files);
  const files = useVersionAwareFiles(readOnly);
  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  const [formData, setFormData] =
    useState<ConstructionAspectsData>(initialData);
  const formDataRef = useLatest(formData);
  const [selectedImage, setSelectedImage] = useState<ConstructionImage | null>(
    null,
  );
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState<{
    [key: string]: boolean;
  }>({});

  const dispatch = useAppDispatch();

  // Fetch existing files
  useEffect(() => {
    dispatch(
      fetchFiles({
        siteId,
        reportId,
      }),
    );
  }, [dispatch, siteId, reportId]);

  // Load version data when viewing historical version
  const currentSiteData = useVersionAwareSiteData(readOnly);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes
  // before the debounced server save fires (e.g. offline, tab closed, phone
  // loses power).
  const draftKey =
    !readOnly && reportId ? `report:${reportId}:construction-aspects` : null;
  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );
  const draftRestoredRef = useRef(false);

  // Restore any unsaved draft once on mount, before server data can
  // overwrite it below.
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once local form state matches the confirmed server record, the
  // IndexedDB draft is redundant — clear it so a future mount doesn't
  // restore stale local data over a fresher server record.
  useEffect(() => {
    if (!draftKey || !currentSiteData) return;
    if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  useEffect(() => {
    // Update session progress
    const prog = sharedConstructionAspectsProgress(formData);
    setSessionProgress((prev) => ({
      ...prev,
      [templateId]: { ...prev[templateId], "construction-aspects": prog },
    }));
  }, [formData, setSessionProgress, templateId]);

  // Save handler
  const handleSave = useCallback(
    async (data: ConstructionAspectsData) => {
      try {
        const mutationResult1 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,
              siteDataInfo: { constructionAspectsData: data },
            }),
          ),
        );
        // Show success notification with custom options
        const config = notificationHelpers.formSaveSuccess(
          "Construction Aspects Data",
          [mutationResult1],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        console.error("[v0] ProjectData save failed:", error);
        const config = notificationHelpers.formSaveError(
          "Project Data",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);
        throw error;
      }
    },
    [dispatch, siteId, notify],
  );

  // Debounced save
  const {
    isSaving: isFormSaving,
    lastSaved: lastSavedForm,
    hasUnsavedChanges: hasFormUnsavedChanges,
    saveError: formSaveError,
    forceSave: forceFormSave,
    resetBaseline: resetFormBaseline,
  } = useDebouncedSave(formData, {
    delay: 7500,
    onSave: handleSave,
    onError: (error) => console.error("Save error:", error),
    readOnly,
  });

  useEffect(() => {
    if (currentSiteData) {
      const newFormData = {
        ...formDataRef.current,
        ...(currentSiteData?.constructionAspectsData || {}),
      };
      resetFormBaseline(newFormData);
      setFormData(newFormData);
    }
  }, [currentSiteData, resetFormBaseline, formDataRef]);

  // Save handler for image descriptions
  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const updates = Object.entries(descriptions);
        const mutationResult2 = await Promise.all(
          updates.map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );
        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult2],
        );
        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );
        notify(config.message, config.type, config.options);

        throw error;
      }
    },
    [dispatch, notify],
  );

  // Debounced save for image descriptions (5000ms delay)
  const {
    isSaving: isImageSaving,
    lastSaved: lastSavedImage,
    hasUnsavedChanges: hasImageUnsavedChanges,
    saveError: imageSaveError,
    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,
    onSave: handleSaveImages,
    onError: (error) => console.error("Image description save error:", error),
    readOnly,
  });

  // Combine form and image save states
  const isSaving = isFormSaving || isImageSaving;
  const hasUnsavedChanges = hasFormUnsavedChanges || hasImageUnsavedChanges;
  const saveError = formSaveError || imageSaveError;
  const lastSaved = useMemo(() => {
    if (!lastSavedForm) return lastSavedImage;
    if (!lastSavedImage) return lastSavedForm;
    return lastSavedForm > lastSavedImage ? lastSavedForm : lastSavedImage;
  }, [lastSavedForm, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();
    forceImageSave();
  }, [forceFormSave, forceImageSave]);

  // Field change handler
  const handleFieldChange = useCallback(
    (field: keyof ConstructionAspectsData) => {
      return (value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
      };
    },
    [],
  );

  // Image upload handler
  const handleImageUpload = useCallback(
    (files: FileList | File[] | null, filterData) => {
      if (!files) return;
      const fileArray = Array.from(files);

      const uploadKey = JSON.stringify(filterData);
      setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));

      requireMutationSuccess(
        dispatch(
          uploadMultipleFiles({
            files: fileArray,
            metadata: {
              siteId: siteId,
              entityType: `construction-aspects`,
              entityId: reportId,
              reportId,
            },
            filterData,
          }),
        ),
      )
        .then((mutationOutcome) => {
          fileArray.forEach((file) => {
            const config = notificationHelpers.imageUploadSuccess(
              file.name,
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          });
        })
        .catch((error) => {
          const config = notificationHelpers.imageUploadError(
            fileArray.length === 1
              ? fileArray[0].name
              : `${fileArray.length} files`,
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
        });
    },
    [siteId, reportId, dispatch, notify],
  );

  // Update image description — updates local state; debounced save handles the dispatch
  const updateImageDescription = useCallback(
    (imageId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
    },
    [setImageDescriptions],
  );

  // Remove image handlers
  const handleRemoveImage = useCallback((imageId: string) => {
    setImageToDelete(imageId);
  }, []);

  const confirmRemoveImage = useCallback(() => {
    if (readOnly) {
      const config = notificationHelpers.readOnlyMode();
      notify(config.message, config.type, config.options);
      setImageToDelete(null);
      return;
    }
    if (imageToDelete) {
      requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "Image",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "Image",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setImageToDelete(null);
        });
    }
  }, [imageToDelete, dispatch, readOnly, notify]);

  const cancelRemoveImage = useCallback(() => {
    setImageToDelete(null);
  }, []);

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
    );
  };

  // Get loading status
  const getLoadingStatus = () => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (hasUnsavedChanges) return "unsaved";
    if (lastSaved) return "saved";
    return "idle";
  };

  // Check if uploading for a section
  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);
    return uploadingImages[uploadKey] || false;
  };

  // Image Card Component
  const ImageCard = useStableMemoComponent(
    function ImageCard({
      image,
      onView,
      onRemove,
      onDescriptionUpdate,
      isDeleting,
      imageDescription,
    }: {
      image: ConstructionImage;
      onView: (image: ConstructionImage) => void;
      onRemove: (id: string) => void;
      onDescriptionUpdate: (id: string, description: string) => void;
      isDeleting: boolean | string;
      imageDescription: string;
    }) {
      return (
        <div key={image.id} className="relative group">
          <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative dark:bg-muted">
            <OfflineImage
              fileId={image?.id}
              src={image?.fileUrl}
              alt="Construction aspects image"
              className="w-full h-full object-cover"
            />
            {loading && (isDeleting === true || isDeleting === image.id) && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
              onClick={() => onView(image)}
              disabled={loading}
              title="View Image"
            >
              <Eye className="w-3 h-3" />
            </Button>
            {!readOnly && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                onClick={() => onRemove(image.id)}
                disabled={loading}
                title="Delete Image"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate truncate">
                {image?.originalName}
              </span>
              <Badge variant="outline" className="text-xs">
                {formatFileSize(image?.fileSize)}
              </Badge>
            </div>
            <OptimizedTextarea
              id={`desc-${image.id}`}
              label=""
              value={imageDescription}
              readOnly={readOnly}
              onChange={(value) => onDescriptionUpdate(image.id, value)}
              placeholder="Describe this construction aspect..."
              rows={2}
              disabled={loading}
            />
            <div className="text-xs text-slate-500 dark:text-muted-foreground">
              Date: {new Date(image.uploadDate).toLocaleDateString()}
            </div>
          </div>
        </div>
      );
    },
    (prevProps, nextProps) => {
      // Return true if props are equal (no re-render), false if different (re-render)
      // Re-render only if image ID, description, or isDeleting actually changed
      const propsEqual =
        prevProps.image.id === nextProps.image.id &&
        prevProps.imageDescription === nextProps.imageDescription &&
        prevProps.isDeleting === nextProps.isDeleting;
      return propsEqual; // true = skip re-render, false = re-render
    },
  );

  // Image Section Component
  const ImageSection = useStableMemoComponent(function ImageSection({
    title,
    filterData,
    uploadFilterData,
    emptyMessage,
    buttonText = "Add Images",
  }: {
    title: string;
    filterData: any;
    uploadFilterData: any;
    emptyMessage: string;
    buttonText?: string;
  }) {
    const sectionImages = files?.filter(
      (file) =>
        file?.relatedTo?.filterData &&
        JSON.stringify(file?.relatedTo?.filterData) ===
          JSON.stringify(filterData),
    );

    const isUploading = isUploadingForSection(uploadFilterData);

    return (
      <div className="space-y-4 pt-4 border-t">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium">{title}</h4>
          {!readOnly && (
            <ImageCapturePicker
              buttonText={buttonText}
              isUploading={isUploading}
              disabled={loading}
              onConfirm={(selectedFiles) =>
                handleImageUpload(selectedFiles, uploadFilterData)
              }
            />
          )}
        </div>

        {isUploading && (
          <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
              <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
            </div>
          </div>
        )}

        {!isUploading && sectionImages.length === 0 ? (
          <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
            <div className="flex items-center gap-3">
              <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                  No images added
                </p>
                <p className="text-xs text-slate-500 dark:text-muted-foreground">{emptyMessage}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sectionImages.map((image) => (
              <ImageCard
                key={image.id}
                image={image}
                imageDescription={
                  imageDescriptions[image.id] ?? image?.fileDescription ?? ""
                }
                onView={setSelectedImage}
                onRemove={handleRemoveImage}
                onDescriptionUpdate={updateImageDescription}
                isDeleting={loading ? imageToDelete : false}
              />
            ))}
          </div>
        )}
      </div>
    );
  });

  return (
    <div className="space-y-4 md:space-y-6 relative">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}
          sectionName="construction-aspects"
          siteId={siteId}
          reportId={reportId}
          readOnly={readOnly}
          setFormData={setFormData}
        />
      )}

      {/* Floating save section - hidden when viewing read-only version */}
      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}
            {false && (
              <div
                className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}
                      lastSaved={lastSaved}
                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}
                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}
            <Button
              onClick={forceSave}
              disabled={isSaving || readOnly}
              className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />
                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}
              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}
              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}
      {/* Construction Aspects Card */}
      <Card>
        <CardHeader>
          <CardTitle>Construction Aspects</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <OptimizedTextarea
            id="constructionAccessDescription"
            label="Construction access and laydown arrangements"
            value={formData.constructionAccessDescription}
            onChange={handleFieldChange("constructionAccessDescription")}
            placeholder="Describe construction access points, laydown areas, and material storage..."
            rows={4}
            readOnly={readOnly}
            required
          />

          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <OptimizedCheckbox
                id="accessibleBy38tLorries"
                label="Is the site accessible by 38t lorries?"
                checked={formData.accessibleBy38tLorries}
                onChange={handleFieldChange("accessibleBy38tLorries")}
                readOnly={readOnly}
                description="Tick if this access is available for 38-tonne lorries"
              />

              <OptimizedCheckbox
                id="groundConditionRisks"
                label="Are there ground condition risks?"
                checked={formData.groundConditionRisks}
                onChange={handleFieldChange("groundConditionRisks")}
                readOnly={readOnly}
                description="Tick if ground condition risks are present"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <OptimizedCheckbox
                id="trafficImpact"
                label="Will construction impact local traffic?"
                checked={formData.trafficImpact}
                onChange={handleFieldChange("trafficImpact")}
                readOnly={readOnly}
                description="Tick if local traffic impact is expected"
              />

              <OptimizedCheckbox
                id="offloadingAreaAvailable"
                label="Is an offloading area available?"
                checked={formData.offloadingAreaAvailable}
                onChange={handleFieldChange("offloadingAreaAvailable")}
                readOnly={readOnly}
                description="Tick if a dedicated offloading area is available"
              />
            </div>

            <OptimizedCheckbox
              id="waterPowerAvailability"
              label="Is water and power available on site?"
              checked={formData.waterPowerAvailability}
              onChange={handleFieldChange("waterPowerAvailability")}
              readOnly={readOnly}
              description="Tick if water and power are available on site"
            />
          </div>

          <OptimizedTextarea
            id="otherRemarks"
            label="Other remarks"
            value={formData.otherRemarks}
            onChange={handleFieldChange("otherRemarks")}
            placeholder="Enter any other construction-related remarks..."
            rows={3}
            readOnly={readOnly}
          />

          <ImageSection
            title="Construction Aspects Images"
            filterData={{ construction_aspects: true }}
            uploadFilterData={{ construction_aspects: true }}
            emptyMessage="Add images related to construction aspects"
            buttonText="Add Images"
          />
        </CardContent>
      </Card>

      {/* Construction Aspects Images Card */}
      {/* <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Construction Aspects Images
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.multiple = true;
                input.accept = "image/*";
                input.onchange = (e) => {
                  const target = e.target as HTMLInputElement;
                  handleImageUpload(target.files, { construction_aspects_main: true });
                };
                input.click();
              }}
              disabled={
                loading || isUploadingForSection({ construction_aspects_main: true })
              }
            >
              {isUploadingForSection({ construction_aspects_main: true }) ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              {isUploadingForSection({ construction_aspects_main: true })
                ? "Uploading..."
                : "Add Images"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isUploadingForSection({ construction_aspects_main: true }) && (
            <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50">
              <div className="text-center">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
                <p className="text-sm text-slate-600">
                  Uploading construction aspects images...
                </p>
              </div>
            </div>
          )}

          {!isUploadingForSection({ construction_aspects_main: true }) &&
          files?.filter(
            (file) =>
              file?.relatedTo?.entityType === "construction-aspects" &&
              file?.relatedTo?.entityId === currentSite?.id &&
              file?.relatedTo?.filterData?.construction_aspects_main
          ).length === 0 ? (
            <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50">
              <div className="flex items-center gap-3">
                <Camera className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    No construction aspects images yet
                  </p>
                  <p className="text-xs text-slate-500">
                    Add images to document construction aspects
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {files
                ?.filter(
                  (file) =>
                    file?.relatedTo?.entityType === "construction-aspects" &&
                    file?.relatedTo?.entityId === currentSite?.id &&
                    file?.relatedTo?.filterData?.construction_aspects_main
                )
                .map((image) => (
                  <ImageCard
                    key={image.id}
                    image={image}
                    onView={setSelectedImage}
                    onRemove={handleRemoveImage}
                    onDescriptionUpdate={updateImageDescription}
                    isDeleting={loading ? imageToDelete : false}
                  />
                ))}
            </div>
          )}
        </CardContent>
      </Card> */}

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Delete Image</h3>
            </div>
            <p className="text-slate-600 dark:text-muted-foreground mb-6">
              Are you sure you want to delete this image? This action cannot be
              undone.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={cancelRemoveImage}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRemoveImage}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedImage.originalName}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedImage(null)}
                disabled={loading}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4">
              <OfflineImage
                fileId={
                  selectedImage?._id ||
                  selectedImage?.id ||
                  selectedImage?._clientId
                }
                src={selectedImage?.fileUrl}
                alt={selectedImage.originalName}
                className="max-w-full max-h-96 mx-auto"
              />
              {selectedImage.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedImage.fileDescription}
                  </p>
                </div>
              )}
              <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                {new Date(selectedImage.uploadDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress footer */}
      <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Form Status:
            </span>
            <Badge variant="default">Complete</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
              Progress:
            </span>
            <Badge variant="outline">
              {sessionProgress?.[templateId]?.["construction-aspects"] ||
                sharedConstructionAspectsProgress(formData)}
              %
            </Badge>
          </div>
        </div>
        <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
          Auto-save enabled • Changes saved automatically
        </div>
      </div>
    </div>
  );
});
